import { useState } from "react";
import { X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import PhoneInput, { isValidPhone } from "@/components/app/PhoneInput";

const field = "w-full mt-1 mb-3 px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none";

export default function EditCandidateDialog({ candidate, countryCode, onClose, onSaved }) {
  const [form, setForm] = useState({ name: candidate.name || "", email: candidate.email || "", phone: candidate.phone || "" });
  const [busy, setBusy] = useState(false);
  const emailChanged = form.email.trim().toLowerCase() !== (candidate.email || "").toLowerCase();

  const save = async (e) => {
    e.preventDefault();
    if (!isValidPhone(form.phone)) { toast.error("Enter a valid phone number"); return; }
    setBusy(true);
    try {
      const { data } = await api.patch(`/candidates/${candidate.id}`, { name: form.name.trim(), email: form.email.trim(), phone: form.phone || null });
      toast.success(data.invite_resent ? `Saved. Invite sent to ${data.candidate.email}` : "Candidate updated");
      onSaved?.(data.candidate); onClose();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => !busy && onClose()}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="tp-card p-6 w-full max-w-md" data-testid="edit-candidate-form">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-xl font-semibold">Edit Candidate</h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-white" data-testid="edit-cand-close"><X className="w-5 h-5" /></button>
        </div>
        <label className="text-xs text-slate-400 uppercase tracking-wider">Name</label>
        <input data-testid="edit-cand-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} />
        <label className="text-xs text-slate-400 uppercase tracking-wider">Email</label>
        <input data-testid="edit-cand-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={field} />
        {emailChanged && <div className="-mt-1 mb-3 text-xs text-indigo-600" data-testid="edit-cand-email-note">The invite link will be emailed to the new address.</div>}
        <label className="text-xs text-slate-400 uppercase tracking-wider">Phone (optional)</label>
        <div className="mt-1 mb-5"><PhoneInput testid="edit-cand-phone" value={form.phone} defaultCountry={countryCode} onChange={(phone) => setForm({ ...form, phone })} /></div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-300 hover:text-white" data-testid="edit-cand-cancel">Cancel</button>
          <button type="submit" disabled={busy} data-testid="edit-cand-save" className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-60">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save
          </button>
        </div>
      </form>
    </div>
  );
}
